import { useState, useRef, useEffect } from 'react'

export interface SortOption {
  label: string
  value: string
  column: string
  order: 'asc' | 'desc'
}

const sortOptions: SortOption[] = [
  { label: 'Newest First', value: 'created_time-desc', column: 'created_time', order: 'desc' },
  { label: 'Oldest First', value: 'created_time-asc', column: 'created_time', order: 'asc' },
  { label: 'Recently Modified', value: 'modified_time-desc', column: 'modified_time', order: 'desc' },
  { label: 'Least Recently Modified', value: 'modified_time-asc', column: 'modified_time', order: 'asc' },
  { label: 'Name (A-Z)', value: 'name-asc', column: 'name', order: 'asc' },
  { label: 'Name (Z-A)', value: 'name-desc', column: 'name', order: 'desc' },
]

const STORAGE_KEY = 'fastnotes_sort_preference'

interface SortDropdownProps {
  onSortChange: (column: string, order: 'asc' | 'desc') => void
  className?: string
}

export default function SortDropdown({ onSortChange, className = '' }: SortDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Load saved preference from localStorage
  const getSavedSort = (): SortOption => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        const found = sortOptions.find(opt => opt.column === parsed.column && opt.order === parsed.order)
        if (found) return found
      }
    } catch {
      // Ignore errors
    }
    return sortOptions[0] // Default: Newest First
  }

  const [selectedSort, setSelectedSort] = useState<SortOption>(getSavedSort)

  // Notify parent of initial sort on mount
  useEffect(() => {
    onSortChange(selectedSort.column, selectedSort.order)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option: SortOption) => {
    setSelectedSort(option)
    setIsOpen(false)

    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ column: option.column, order: option.order }))
    } catch {
      // Ignore errors
    }

    // Notify parent
    onSortChange(option.column, option.order)
  }

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notes-sort-btn"
        className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
          isOpen
            ? 'bg-primary text-white'
            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-300'
        }`}
        title="Sort notes"
      >
        {/* Sort icon */}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
        </svg>
        <span className="hidden sm:inline">{selectedSort.label}</span>
        {/* Chevron */}
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option)}
              className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center justify-between ${
                selectedSort.value === option.value ? 'text-primary font-medium bg-primary-50' : 'text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2">
                {/* Direction icon */}
                {option.order === 'asc' ? (
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
                {option.label}
              </span>
              {/* Checkmark for selected */}
              {selectedSort.value === option.value && (
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
